import { FC } from 'react';

import { ApolloError, QueryResult, useQuery } from '@apollo/client';

import { SALE_FINAL_BIDS_QUERY, SALE_OFFERINGS_QUERY } from 'query/sales';
import {
  SaleFinalBidsQuery,
  SaleFinalBidsQueryVariables,
  SaleOfferings,
} from 'query/__generated__/sales';

import { labelledPools, SaleBidsTable } from './SaleBidsTable';
import { centralTimeParts } from './salesUtils';

type FinalBid = SaleFinalBidsQuery['saleBids'][number];

const BIDS_UNDER_REVIEW_COPY =
  'Your bids are under review. The FDIC notifies winning and non-winning bidders after it approves the sale.';
const NO_BID_IN_ROUND_COPY = "You didn't submit a bid in this round.";
const DATA_ROOM_ACCESS_ENDED_COPY = 'Data room access ended.';
// SOW §3.8b (p.43): "No information beyond a sale announcement is provided prior to execution of
// the Confidentiality Agreement." loan-service takes no acknowledgement once access ends.
export const CLOSED_WITHOUT_ACCESS_DOCUMENTS_COPY =
  "Bidding closed before you accepted all of this sale's access documents (the Confidentiality Agreement and the Purchaser Eligibility Certification summary), and they can't be accepted now. Without them, nothing beyond the sale announcement is available to you, including your company's bids.";

// api's saleBids refusal of a user who hasn't accepted the sale's access documents.
export const SALE_DOCUMENTS_REQUIRED_REASON = 'sale_documents_required';

const isAccessDocumentsRefusal = (error: ApolloError | undefined): boolean =>
  !!error?.graphQLErrors.some(
    (graphQLError) =>
      graphQLError.extensions?.code === 'FORBIDDEN' &&
      graphQLError.extensions?.reason === SALE_DOCUMENTS_REQUIRED_REASON,
  );

const AccessDocumentsMissing: FC = () => (
  <p data-testid="sale-closed-documents-required" className={`mt-2 text-sm ${MUTED_TEXT}`}>
    {CLOSED_WITHOUT_ACCESS_DOCUMENTS_COPY}
  </p>
);

const biddingClosedCopy = (closedAt: string): string =>
  `Bidding closed ${centralTimeParts(closedAt)?.central ?? '-'}`;

const isFullyLabelled = (bid: FinalBid): boolean =>
  labelledPools(bid, []).every((pool) => !!pool.fdic_pool_number);

const MUTED_TEXT = 'text-gray-400 light:text-gray-700';
const HEADING = 'font-heebo text-lg font-medium text-white light:text-gray-900';

type FinalBidsResult = QueryResult<SaleFinalBidsQuery, SaleFinalBidsQueryVariables>;

const LoadingBids: FC = () => (
  <p data-testid="sale-closed-bids-loading" className={`mt-2 text-sm ${MUTED_TEXT}`}>
    Loading your bids…
  </p>
);

const FinalBids: FC<{ saleId: string; bids: FinalBidsResult }> = ({ saleId, bids }) => {
  const ownBids = bids.data?.saleBids ?? [];
  const needsOfferings = ownBids.some((bid) => !isFullyLabelled(bid));
  const offerings = useQuery<SaleOfferings>(SALE_OFFERINGS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
    skip: !needsOfferings,
  });

  if (needsOfferings && offerings.loading && !offerings.data) {
    return <LoadingBids />;
  }
  if (bids.error && !bids.data) {
    return (
      <p className="mt-2 text-sm text-status-error light:text-error-muted">
        Failed to load your bids.
      </p>
    );
  }
  if (ownBids.length === 0) {
    return (
      <p data-testid="sale-closed-no-bids" className={`mt-2 text-sm ${MUTED_TEXT}`}>
        {NO_BID_IN_ROUND_COPY}
      </p>
    );
  }
  return (
    <>
      <SaleBidsTable
        saleId={saleId}
        bids={ownBids.map((bid) => ({
          ...bid,
          pools: labelledPools(bid, offerings.data?.saleOfferings ?? []),
        }))}
        linkPools={false}
        showBidder={false}
        tableTestId="sale-closed-bids"
        rowTestId="sale-closed-bid"
      />
      <p data-testid="sale-bids-under-review" className={`mt-4 text-sm ${MUTED_TEXT}`}>
        {BIDS_UNDER_REVIEW_COPY}
      </p>
    </>
  );
};

const BiddingClosedBanner: FC<{ closedAt: string }> = ({ closedAt }) => (
  <div
    data-testid="sale-bidding-closed-banner"
    className="mt-6 rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-gray-100"
  >
    <p className="font-heebo text-base font-medium text-white light:text-gray-900">
      {biddingClosedCopy(closedAt)}
    </p>
  </div>
);

// After the deadline, for a user of an approved bidder without the CA or the PEC summary: the gate
// is gone, since loan-service would refuse the acknowledgement, and so is everything past the
// announcement.
export const SaleBiddingClosedWithoutAccessDocuments: FC<{ closedAt: string }> = ({ closedAt }) => (
  <>
    <BiddingClosedBanner closedAt={closedAt} />
    <AccessDocumentsMissing />
  </>
);

// The bidder's summary once the sale's last deadline has passed (RFP §3.11, BI ¶15). Their own
// final bids, read-only, are on the Bids tab, with no result: the FDIC announces winners only after
// it approves the sale.
export const SaleBiddingClosedSummary: FC<{ closedAt: string }> = ({ closedAt }) => (
  <>
    <BiddingClosedBanner closedAt={closedAt} />

    <section className="mt-6" aria-labelledby="sale-closed-data-room-heading">
      <h2 id="sale-closed-data-room-heading" className={HEADING}>
        Data room
      </h2>
      <p data-testid="sale-data-room-ended" className={`mt-2 text-sm ${MUTED_TEXT}`}>
        {DATA_ROOM_ACCESS_ENDED_COPY}
      </p>
    </section>
  </>
);

// api's rule governs: it may refuse the bids to a user the page took to have both documents.
export const SaleFinalBids: FC<{ saleId: string }> = ({ saleId }) => {
  const bids = useQuery<SaleFinalBidsQuery, SaleFinalBidsQueryVariables>(SALE_FINAL_BIDS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
  });
  if (isAccessDocumentsRefusal(bids.error)) {
    return (
      <div className="mt-6">
        <AccessDocumentsMissing />
      </div>
    );
  }
  // Until api answers, the tab may yet hold only that line.
  if (bids.loading && !bids.data) {
    return (
      <div className="mt-6">
        <LoadingBids />
      </div>
    );
  }
  return (
    <section className="mt-6" aria-labelledby="sale-closed-bids-heading">
      <h2 id="sale-closed-bids-heading" className={HEADING}>
        Your final bids
      </h2>
      <FinalBids saleId={saleId} bids={bids} />
    </section>
  );
};
